import { useState, useEffect, useCallback, useRef } from 'react';
import { ApiError } from '../services/api';
import axios, { AxiosError, CancelToken } from 'axios';
import { ApiErrorType, ApiErrorData, PaginatedResponse } from '../types/api';

interface UseApiOptions<T> {
  onSuccess?: (data: T) => void;
  onError?: (error: Error) => void;
  autoExecute?: boolean;
  maxRetries?: number;
}

interface UseApiState<T> {
  data: T | null;
  error: Error | null;
  isLoading: boolean;
  retryCount: number;
}

const toApiError = (error: unknown): ApiError => {
  if (error instanceof ApiError) {
    return error;
  }
  return new ApiError(
    ApiErrorType.UNKNOWN,
    (error as Error)?.message || 'An unknown error occurred',
    (error as AxiosError<ApiErrorData>)?.response?.status,
    error
  );
};

export function useApi<T>(
  apiCall: (cancelToken?: CancelToken) => Promise<T>,
  options: UseApiOptions<T> = {}
) {
  const { onSuccess, onError, autoExecute = true, maxRetries = 3 } = options;

  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    error: null,
    isLoading: autoExecute,
    retryCount: 0,
  });

  const cancelSourceRef = useRef(axios.CancelToken.source());
  const isMountedRef = useRef(true);
  const apiCallRef = useRef(apiCall);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    apiCallRef.current = apiCall;
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  });

  // Returns true when the call succeeded, false on error or cancel.
  const execute = useCallback(async (): Promise<boolean> => {
    cancelSourceRef.current.cancel('Superseded by a new request');
    cancelSourceRef.current = axios.CancelToken.source();

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const data = await apiCallRef.current(cancelSourceRef.current.token);
      if (!isMountedRef.current) return false;
      setState(prev => ({ ...prev, data, isLoading: false, error: null }));
      onSuccessRef.current?.(data);
      return true;
    } catch (error) {
      if (!isMountedRef.current) return false;

      if (axios.isCancel(error)) {
        setState(prev => ({ ...prev, isLoading: false }));
        return false;
      }

      const apiError = toApiError(error);
      setState(prev => ({ ...prev, error: apiError, isLoading: false }));
      onErrorRef.current?.(apiError);
      return false;
    }
  }, []);

  const retry = useCallback(() => {
    if (state.retryCount >= maxRetries) {
      return;
    }
    setState(prev => ({ ...prev, retryCount: prev.retryCount + 1 }));
    execute();
  }, [execute, state.retryCount, maxRetries]);

  const reset = useCallback(() => {
    setState({ data: null, error: null, isLoading: false, retryCount: 0 });
  }, []);

  useEffect(() => {
    if (autoExecute) {
      isMountedRef.current = true;
      execute();
    }
    return () => {
      isMountedRef.current = false;
      cancelSourceRef.current.cancel('Component unmounted');
    };
  }, [autoExecute, execute]);

  return {
    ...state,
    execute,
    retry,
    reset,
    cancel: () => cancelSourceRef.current.cancel('Request cancelled'),
  };
}

export interface UsePaginatedApiState<T> extends UseApiState<T[]> {
  hasMore: boolean;
  isLoadingMore: boolean;
}

interface UsePaginatedApiOptions<T> extends UseApiOptions<T[]> {
  pageSize?: number;
}

export interface PaginatedRequestParams {
  limit: number;
  page: number;
  lastKey?: string;
  [key: string]: any;
}

export function usePaginatedApi<T>(
  apiCall: (
    params: PaginatedRequestParams,
    cancelToken?: CancelToken
  ) => Promise<PaginatedResponse<T>>,
  options: UsePaginatedApiOptions<T> = {}
) {
  const { pageSize = 20, onSuccess, onError, autoExecute = true } = options;

  const [state, setState] = useState<UsePaginatedApiState<T>>({
    data: [],
    error: null,
    isLoading: autoExecute,
    isLoadingMore: false,
    hasMore: true,
    retryCount: 0,
  });

  const pageRef = useRef(1);
  const lastKeyRef = useRef<string | null>(null);
  const cancelSourceRef = useRef(axios.CancelToken.source());
  const isMountedRef = useRef(true);
  const apiCallRef = useRef(apiCall);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    apiCallRef.current = apiCall;
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  });

  const runPage = useCallback(
    async (append: boolean, overrides: Record<string, any> = {}) => {
      cancelSourceRef.current.cancel('Superseded by a new request');
      cancelSourceRef.current = axios.CancelToken.source();

      if (append) {
        setState(prev => ({ ...prev, isLoadingMore: true, error: null }));
      } else {
        pageRef.current = 1;
        lastKeyRef.current = null;
        setState(prev => ({
          ...prev,
          isLoading: true,
          isLoadingMore: false,
          error: null,
          data: [],
        }));
      }

      const requestPage = append ? pageRef.current + 1 : 1;
      const params: PaginatedRequestParams = {
        limit: pageSize,
        page: requestPage,
        ...overrides,
      };
      if (lastKeyRef.current) {
        params.lastKey = lastKeyRef.current;
      }

      try {
        const response = await apiCallRef.current(
          params,
          cancelSourceRef.current.token
        );
        if (!isMountedRef.current) return;

        const items = response?.items ?? [];
        const hasMore = response?.hasMore ?? !!response?.lastEvaluatedKey;
        lastKeyRef.current = response?.lastEvaluatedKey || null;
        pageRef.current = requestPage;

        setState(prev =>
          append
            ? {
                ...prev,
                data: [...(prev.data ?? []), ...items],
                isLoadingMore: false,
                hasMore,
                error: null,
              }
            : {
                ...prev,
                data: items,
                isLoading: false,
                hasMore,
                error: null,
              }
        );

        if (!append) {
          onSuccessRef.current?.(items);
        }
      } catch (error) {
        if (!isMountedRef.current) return;

        if (axios.isCancel(error)) {
          setState(prev => ({
            ...prev,
            isLoading: false,
            isLoadingMore: false,
          }));
          return;
        }

        const apiError = toApiError(error);
        setState(prev => ({
          ...prev,
          error: apiError,
          isLoading: false,
          isLoadingMore: false,
        }));
        onErrorRef.current?.(apiError);
      }
    },
    [pageSize]
  );

  const execute = useCallback(
    (overrides?: Record<string, any>) => runPage(false, overrides ?? {}),
    [runPage]
  );

  const loadMore = useCallback(() => {
    if (!state.hasMore || state.isLoadingMore) return;
    return runPage(true);
  }, [state.hasMore, state.isLoadingMore, runPage]);

  const reset = useCallback(() => {
    setState({
      data: [],
      error: null,
      isLoading: false,
      isLoadingMore: false,
      hasMore: true,
      retryCount: 0,
    });
    pageRef.current = 1;
    lastKeyRef.current = null;
  }, []);

  const retry = useCallback(() => {
    execute();
  }, [execute]);

  useEffect(() => {
    if (autoExecute) {
      isMountedRef.current = true;
      execute();
    }
    return () => {
      isMountedRef.current = false;
      cancelSourceRef.current.cancel('Component unmounted');
    };
  }, [autoExecute, execute]);

  return {
    ...state,
    execute,
    loadMore,
    retry,
    reset,
    cancel: () => cancelSourceRef.current.cancel('Request cancelled'),
  };
}
